import { useMemo } from 'preact/hooks';

import type { Post } from '../../../model/post.interface';
import { PerksHelper } from '../../../perks.helper';

interface UseTruncateDescriptionOptions {
  post: Post;
  collapseLongPosts?: boolean;
}

interface TruncateResult {
  truncatedDescription: string;
  showContReading: boolean;
}

export function useTruncateDescription({
  post,
  collapseLongPosts = true,
}: UseTruncateDescriptionOptions): TruncateResult {
  return <TruncateResult>useMemo(() => {
    const fullText = PerksHelper.isProduct(post.contents)
      ? post?.textContent || post?.contents[0]?.product?.description
      : post?.textContent;

    if (!fullText) {
      return {
        truncatedDescription: '',
        showContReading: false,
      };
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(fullText, 'text/html');
    const bodyNodes = Array.from(doc.body.childNodes);
    const totalParagraphs = doc.querySelectorAll('p').length;

    let paragraphCount = 0;
    const nodesToKeep: string[] = [];

    for (const node of bodyNodes) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        if (element.tagName.toLowerCase() === 'p') {
          paragraphCount++;
        }
      }

      nodesToKeep.push((node as HTMLElement).outerHTML || node.textContent || '');

      if (paragraphCount >= 2) {
        break;
      }
    }

    const showContReading = totalParagraphs > 2 && collapseLongPosts;
    const truncatedDescription = showContReading ? nodesToKeep.join('') : fullText;

    return {
      truncatedDescription,
      showContReading,
    };
  }, [post, collapseLongPosts]);
}
